/**
 * Pure-function utilities for Guest Connect campaign business logic.
 * Mirrors §7.3 Guest Connect Business Rules in FUNCTIONAL_DOCUMENTATION.md.
 */

import {
  ICampaign,
  ICampaignQuestion,
  ICampaignResponse,
  ICampaignAnswer,
  CampaignStatus,
  QuestionType,
  OwnershipType,
} from '../interface/ICampaign';
import { getLocalTodayString, toDateOnlyString } from './dateUtils';

// ─── Campaign status resolution ───────────────────────────────────────────────

/**
 * Derives the CampaignStatus from dates vs. the current moment.
 * @param now  - Override for "now" (useful for deterministic tests).
 */
export function resolveCampaignStatus(
  startDate: string,
  endDate: string,
  now: Date = new Date(),
): CampaignStatus {
  const todayStr = toDateOnlyString(now);
  const startStr = toDateOnlyString(startDate);
  const endStr = toDateOnlyString(endDate);
  if (todayStr < startStr) return CampaignStatus.SCHEDULED;
  if (todayStr > endStr) return CampaignStatus.PAST;
  return CampaignStatus.ACTIVE;
}

// ─── Campaign date validation ─────────────────────────────────────────────────

/** Validates campaign date range. Returns error messages (empty = valid). */
const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export function validateCampaignDates(
  startDate: string,
  endDate: string,
): string[] {
  const errors: string[] = [];
  const startStr = toDateOnlyString(startDate);
  const endStr = toDateOnlyString(endDate);
  if (!DATE_ONLY_PATTERN.test(startStr)) errors.push('Start date is invalid.');
  if (!DATE_ONLY_PATTERN.test(endStr)) errors.push('End date is invalid.');
  if (!errors.length && endStr <= startStr) {
    errors.push('End date must be after start date.');
  }
  return errors;
}

// ─── Question validation ──────────────────────────────────────────────────────

/** Validates the questions array for a campaign. */
export function validateCampaignQuestions(
  questions: Array<Pick<ICampaignQuestion, 'questionText' | 'questionType'>>,
): string[] {
  const errors: string[] = [];
  if (!questions.length) {
    errors.push('At least one question is required.');
  }
  questions.forEach((q, i) => {
    if (!q.questionText.trim()) {
      errors.push(`Question ${i + 1} text is required.`);
    }
  });
  return errors;
}

// ─── Sentiment / scoring ──────────────────────────────────────────────────────

/**
 * Calculates a sentiment score (0–100) from a set of answers.
 * Only YES_NO and RATING question types contribute.
 *   YES_NO: yes = 100, no = 0
 *   RATING(1–5): scaled to 0–100 ((rating - 1) / 4 × 100)
 */
export function calculateSentimentScore(answers: ICampaignAnswer[]): number {
  const scoreable = answers.filter(
    (a) =>
      a.questionType === QuestionType.YES_NO ||
      a.questionType === QuestionType.RATING,
  );
  if (!scoreable.length) return 0;

  const total = scoreable.reduce((sum, answer) => {
    if (answer.questionType === QuestionType.YES_NO) {
      return sum + (answer.answer === true ? 100 : 0);
    }
    // RATING: scale 1–5 → 0–100
    return sum + (((Number(answer.answer) - 1) / 4) * 100);
  }, 0);

  return Math.round(total / scoreable.length);
}

// ─── Reward eligibility ───────────────────────────────────────────────────────

/**
 * A response is eligible for a reward drawing only if the guest provided
 * a contactable identifier (email or phone).
 */
export function isEligibleForReward(
  response: Pick<ICampaignResponse, 'email' | 'phone'>,
): boolean {
  return !!(response.email?.trim() || response.phone?.trim());
}

// ─── Scope filtering ──────────────────────────────────────────────────────────

/**
 * Returns only campaigns that are visible to the given workplace / organization.
 * Organization-scoped campaigns are visible to all workplaces in that org.
 * Workplace-scoped campaigns are visible only to matching workplaces.
 */
export function filterCampaignsByScope(
  campaigns: ICampaign[],
  workplaceId: string,
  organizationId: string,
): ICampaign[] {
  return campaigns.filter((c) => {
    if (
      c.ownershipType === OwnershipType.ORGANIZATION &&
      c.organizationId === organizationId
    ) {
      return true;
    }
    if (
      c.ownershipType === OwnershipType.WORKPLACE &&
      c.workplaceId === workplaceId
    ) {
      return true;
    }
    return false;
  });
}

// ─── Active-only filter ───────────────────────────────────────────────────────

/** Returns only campaigns currently in ACTIVE status. */
export function getActiveCampaigns(campaigns: ICampaign[]): ICampaign[] {
  return campaigns.filter((c) => c.status === CampaignStatus.ACTIVE);
}
